import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ExpandArrow, PrimitiveCard } from "./PrimitiveCard";
import Panel from "./Panel";
import { ChevronDownIcon, ClipboardIcon, LockClosedIcon, LockOpenIcon } from "@heroicons/react/24/outline";
import AIProcessButton from "./AIProcessButton";
import MainStore from "./MainStore";
import useDataEvent from "./CustomHook";
import SmallButton from "./SmallButton";
import MarkdownEditor, { slateToMarkdown } from "./MarkdownEditor";
import PrimitiveConfig, { flattenStructuredResponse } from "./PrimitiveConfig";
import { buildFieldExtractMarkdownTable, markdownToSlate, modiftyEntries } from "./SharedTransforms";
import clsx from "clsx";
import { Table } from "./Table";



  const processText = (text) => {
    const lines = text.split("\n").filter(d => d && d.length > 0);
    const elements = [];
    const ulClass = "ml-6 list-disc space-y-1";
    let lineCount = 0;

    const createNestedList = (lines, level = 0) => {
        const result = [];
        while (lines.length > 0) {
            const line = lines[0];
            const indentLevel = (line.match(/^-+/) || [""])[0].length;
            const content = line.substring(indentLevel).trim();

            if (indentLevel < level) {
                break;
            }

            lines.shift();

            const boldWholeLineMatch = /^\*\*(.+?)\*\*$/.exec(content);
            let formattedLine;
            if (boldWholeLineMatch) {
                formattedLine = `<strong style='font-size:20px' class="font-bold">${boldWholeLineMatch[1]}</strong>`;
            } else {
                formattedLine = content.replaceAll(/\*\*(.+?)\*\*/g, '<strong class="font-semibold">$1</strong>');
            }

            if (indentLevel === level) {
                result.push(<li key={lineCount++} dangerouslySetInnerHTML={{ __html: formattedLine }} />);
            } else if (indentLevel > level) {
                lines.unshift(line); // Push the line back to process in the next recursion
                result.push(<ul class={ulClass} key={`list-${lineCount}`}>{createNestedList(lines, indentLevel)}</ul>);
            }
        }
        return result;
    };

    while (lines.length > 0) {
        const line = lines[0];
        const indentLevel = (line.match(/^-+/) || [""])[0].length;
        const content = line.substring(indentLevel).trim();

        const boldWholeLineMatch = /^\*\*(.+?)\*\*$/.exec(content);
        let formattedLine;
        if (boldWholeLineMatch) {
            formattedLine = `<strong style='font-size:20px' class="font-bold">${boldWholeLineMatch[1]}</strong>`;
        } else {
            formattedLine = content.replaceAll(/\*\*(.+?)\*\*/g, '<strong class="font-semibold">$1</strong>');
        }

        if (indentLevel === 0) {
            lines.shift();
            if (lines.length > 0 && lines[0].startsWith("-")) {
                const nestedList = createNestedList(lines);
                elements.push(<p key={lineCount++} dangerouslySetInnerHTML={{ __html: formattedLine }} />);
                elements.push(<ul class={ulClass} key={`list-${lineCount}`}>{nestedList}</ul>);
            } else {
                elements.push(<p key={lineCount++} dangerouslySetInnerHTML={{ __html: formattedLine }} />);
            }
        } else {
            elements.push(<ul class={ulClass} key={`list-${lineCount}`}>{createNestedList(lines, indentLevel)}</ul>);
        }
    }

    return [elements, lineCount > 2];
};

  const copyToClipboardOLD = async (divRef) => {
    console.log(divRef)
    if (divRef.current) {
      try {
        const htmlContent = divRef.current.innerHTML;
        const plainTextContent = divRef.current.innerText;

        const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
        const textBlob = new Blob([plainTextContent], { type: 'text/plain' });
        const data = [
            new ClipboardItem({ 'text/html': htmlBlob, 'text/plain': textBlob })
          ];

        await navigator.clipboard.write(data);
      } catch (err) {
        console.error('Failed to copy: ', err);
      }
    }
  };


function buildSectionedSlate(sections, { buildAnchorTableMarkdown, summaryId } = {}){
    if( !sections || sections.length === 0 ){
        return [{ type: 'paragraph', children: [{ text: '' }] }]
    }
    const out = []
    sections.forEach((section, idx)=>{
        out.push({
            type: 'heading',
            level: 2,
            sectionId: idx,
            isHeading: true,
            children: [{ text: typeof section?.heading === 'string' ? section.heading : '' }],
        })
        const contentText = typeof section?.content === 'string' ? section.content : ''
        const contentNodes = markdownToSlate(contentText)
        if( contentNodes.length === 0 ){
            out.push({ type: 'paragraph', sectionId: idx, children: [{ text: '' }] })
        } else {
            contentNodes.forEach((node)=>{
                out.push({ ...node, sectionId: idx })
            })
        }
        if( Array.isArray(section?.ids) && section.ids.length > 0 ){
            const quotes = Array.isArray(section.quote)
                ? section.quote
                : (typeof section.quote === 'string' && section.quote.trim().length ? [section.quote] : [])
            const idMap = section.id_map && typeof section.id_map === 'object' && !Array.isArray(section.id_map)
                ? section.id_map
                : undefined
            // Skip bare "Title" sections when other sections have useful citations
            if (section.heading === "Title" && quotes.length === 0 && !idMap) {
                const hasOtherCitations = sections.some(s =>
                    s.heading !== "Title" && (
                        (Array.isArray(s.quote) && s.quote.length > 0) ||
                        (s.id_map && typeof s.id_map === 'object' && !Array.isArray(s.id_map) && Object.keys(s.id_map).length > 0)
                    )
                );
                if (hasOtherCitations) return;
            }
            const badgeType = (quotes.length > 0 || idMap)
                ? `cite:${encodeURIComponent(JSON.stringify({ ids: section.ids, quotes, id_map: idMap, summaryId: summaryId ?? null }))}`
                : `id:${section.ids.join(", ")}`
            const badgeNode = { type: 'badge', badgeType, children: [{ text: '' }] }
            let lastParagraphIdx = -1
            for( let i = out.length - 1; i >= 0; i-- ){
                if( out[i].sectionId === idx && !out[i].decorative && !out[i].isHeading && out[i].type === 'paragraph' ){
                    lastParagraphIdx = i
                    break
                }
            }
            if( lastParagraphIdx >= 0 ){
                const lastBlock = out[lastParagraphIdx]
                out[lastParagraphIdx] = {
                    ...lastBlock,
                    children: [...lastBlock.children, badgeNode, { text: '' }],
                }
            } else {
                out.push({
                    type: 'paragraph',
                    sectionId: idx,
                    decorative: true,
                    children: [{ text: '' }, badgeNode, { text: '' }],
                })
            }
        }
        if( Array.isArray(section?.anchor_mentions) && section.anchor_mentions.length > 0 && typeof buildAnchorTableMarkdown === 'function' ){
            section.anchor_mentions.forEach((mention)=>{
                const anchorId = mention?.anchor_id
                if( !anchorId ){ return }
                const tableMd = buildAnchorTableMarkdown(anchorId, section)
                if( !tableMd || !tableMd.trim() ){ return }
                const tableNodes = markdownToSlate(tableMd)
                tableNodes.forEach((node)=>{
                    out.push({ ...node, sectionId: idx, decorative: true })
                })
            })
        }
    })
    return out.length ? out : [{ type: 'paragraph', children: [{ text: '' }] }]
}

function serializeSlateToSections(slateValue, sourceSections){
    const next = sourceSections.map((s)=>({ ...s }))
    const groups = new Map()
    slateValue.forEach((node)=>{
        if( !node || node.decorative ){ return }
        const sid = node.sectionId
        if( sid === undefined || sid === null ){ return }
        if( !groups.has(sid) ){ groups.set(sid, { heading: null, contentNodes: [] }) }
        const bucket = groups.get(sid)
        if( node.isHeading ){
            bucket.heading = (node.children ?? []).map((c)=>c?.text ?? '').join('')
        } else {
            const cleaned = stripInlineBadges(node)
            if( cleaned ){
                bucket.contentNodes.push(cleaned)
            }
        }
    })
    const normalize = (md)=>{
        const text = (md ?? '').trim()
        if( !text ){ return '' }
        try{
            return slateToMarkdown(markdownToSlate(text)).trim()
        }catch(e){
            return text
        }
    }
    const changedIndices = []
    next.forEach((section, idx)=>{
        const prevHeading = (sourceSections[idx]?.heading ?? '').trim()
        const prevHeadingNormalized = normalize(prevHeading)
        const prevContentRaw = sourceSections[idx]?.content ?? ''
        const prevContentNormalized = normalize(prevContentRaw)
        if( !groups.has(idx) ){
            // Section not present in the current slate — keep original unchanged.
            // Slate editor may drop decorative / empty blocks; never wipe real data.
            return
        }
        const bucket = groups.get(idx)
        const nextHeadingRaw = (bucket.heading ?? section.heading ?? '').trim()
        const nextHeadingNormalized = normalize(nextHeadingRaw)
        const nextContent = bucket.contentNodes.length ? slateToMarkdown(bucket.contentNodes).trim() : ''
        if( nextHeadingNormalized !== prevHeadingNormalized || nextContent !== prevContentNormalized ){
            section.heading = nextHeadingRaw
            section.content = nextContent
            section.edited = true
            changedIndices.push(idx)
        } else {
            section.heading = nextHeadingRaw
            section.content = prevContentRaw
        }
    })
    return { next, changedIndices }
}

function stripInlineBadges(node){
    if( !node || typeof node !== 'object' ){ return node }
    if( node.type === 'badge' ){ return null }
    if( !Array.isArray(node.children) ){ return node }
    const nextChildren = node.children
        .map((child)=>stripInlineBadges(child))
        .filter((c)=>c !== null)
    return { ...node, children: nextChildren.length ? nextChildren : [{ text: '' }] }
}

function StructuredSummaryEditor({ primitive, sections, isLocked, setIsLocked, buildCounter, summaryRef, className, buildAnchorTableMarkdown }){
    const ownEditorRef = useRef()
    const editorRef = summaryRef ?? ownEditorRef
    const sectionsRef = useRef(sections)
    sectionsRef.current = sections
    const primitiveIdRef = useRef(primitive.id)
    primitiveIdRef.current = primitive.id

    // Signature of the section data we'd render from. While locked, any change to
    // this (e.g. a server-pushed update arriving via useDataEvent in the parent)
    // rebuilds the Slate value. While unlocked we freeze it so an incoming update
    // can't clobber the user's in-progress edit.
    const sectionsSignature = useMemo(()=>{
        return JSON.stringify((sections ?? []).map((s)=>[s?.heading, s?.content, s?.edited, s?.ids]))
    }, [sections])

    const slateValue = useMemo(()=>{
        return buildSectionedSlate(sectionsRef.current, { buildAnchorTableMarkdown, summaryId: primitive.id })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [primitive.id, buildCounter, isLocked ? sectionsSignature : 'editing'])

    const commit = useCallback((value)=>{
        if( !Array.isArray(value) ){ return }
        // Guard: only write if we're still the active editor for this primitive.
        // The markdown editor may fire commit on unmount after the sidebar has
        // switched to a different primitive.
        if( primitiveIdRef.current !== primitive.id ){ return }
        const source = sectionsRef.current ?? []
        const { next, changedIndices } = serializeSlateToSections(value, source)
        if( changedIndices.length === 0 ){ return }
        primitive.setParameter('structured_summary', next)
        const flat = flattenStructuredResponse(next, next)
        primitive.setParameter('summary', flat)
    }, [primitive])

    useEffect(()=>{
        return ()=>{
            if( editorRef.current && typeof editorRef.current.flush === 'function' ){
                editorRef.current.flush()
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [primitive.id])

    const onLockToggle = useCallback(()=>{
        if( !isLocked ){
            if( editorRef.current && typeof editorRef.current.flush === 'function' ){
                editorRef.current.flush()
            }
        }
        setIsLocked((v)=>!v)
    }, [isLocked, setIsLocked])

    const renderHeadingExtra = useCallback((element)=>{
        const sid = element?.sectionId
        if( sid === undefined || sid === null ){ return null }
        const section = (sectionsRef.current ?? [])[sid]
        if( !section?.edited ){ return null }
        return <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 ring-1 ring-inset ring-amber-200">Edited</span>
    }, [])

    return (
        <div className="relative">
            <button
                type="button"
                onClick={onLockToggle}
                className="absolute right-0 top-0 z-10 inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white/90 px-2 py-1 text-[11px] text-gray-500 shadow-sm hover:bg-gray-50 hover:text-gray-700"
                title={isLocked ? 'Unlock to edit' : 'Lock to save'}
            >
                {isLocked
                    ? <><LockClosedIcon className="h-3 w-3"/>Locked</>
                    : <><LockOpenIcon className="h-3 w-3 text-emerald-600"/>Editing</>
                }
            </button>
            <MarkdownEditor
                ref={editorRef}
                initialSlateValue={slateValue}
                onCommit={commit}
                readOnly={isLocked}
                renderHeadingExtra={renderHeadingExtra}
                className={className}
            />
        </div>
    )
}

export default function SummaryCard({primitive, ...props}){
    useDataEvent('relationship_update set_parameter set_field',primitive.id)
    const [preview, setPreview] = useState(true)
    const [longSummary, setLongSummary] = useState(false)
    const [isLocked, setIsLocked] = useState(true)
    const [slateBuildCounter, setSlateBuildCounter] = useState(0)
    const summaryRef = useRef()
    const prevLockRef = useRef(true)
    useEffect(()=>{
        setIsLocked(true)
        setSlateBuildCounter((c)=>c + 1)
    }, [primitive.id])
    useEffect(()=>{
        if( !prevLockRef.current && isLocked ){
            setSlateBuildCounter((c)=>c + 1)
        }
        prevLockRef.current = isLocked
    }, [isLocked])
    const results = primitive.primitives.uniqueAllItems

    let summary = <></>
    let renderedAsTimelineTable = false
    const summaryMarkdownClassName = "[&>p]:my-3"

    const timelineSummaryCategoryId = PrimitiveConfig.Constants.TIMELINE_EVENT_SUMMARY ?? 178
    const isTimelineSummary = primitive.referenceId === timelineSummaryCategoryId
    const normalizeTimelineCell = (value)=>{
        if( value === undefined || value === null ){
            return ""
        }
        if( Array.isArray(value) ){
            return value.map((entry)=>String(entry ?? "").trim()).filter(Boolean).join(" | ")
        }
        return String(value).trim()
    }
    const isPlaceholderTimelineCell = (value)=>{
        const text = normalizeTimelineCell(value).toLowerCase()
        if( !text ){
            return true
        }
        return (
            text === "no relevant data" ||
            text.includes("date of the event in yyyy-mm-dd format") ||
            text.includes("short lowercase taxonomy for the event type") ||
            text.includes("one-sentence factual description of what happened") ||
            text.includes("primary source organization or publication for this event")
        )
    }
    const timelineReservedMetaFields = new Set([
        "company",
        "market_scope",
        "focus_dimension",
        "meeting_stakeholders",
        "stakeholder_roles",
        "focus_business_unit",
        "focus_product_line",
        "focus_market_segment",
        "focus_region",
        "focus_customer_type",
        "focus_roles",
        "meeting_signal_topics",
        "focus_strict",
        "background_context",
    ])
    const timelineFixedFields = new Set([
        "id",
        "date",
        "name",
        "event_type",
        "event",
        "rationale",
        "deal",
        "source",
        "overview",
        "quote",
        "ids",
    ])
    const titleFromField = (field)=>field.replaceAll("_", " ").replace(/\b\w/g, (match)=>match.toUpperCase())
    const hasMeaningfulTimelineContent = (row)=>{
        const cells = Object.entries(row)
            .filter(([key])=>key !== "id" && key !== "ids" && !timelineReservedMetaFields.has(key))
            .map(([, value])=>value)
        return cells.some((cell)=>!isPlaceholderTimelineCell(cell))
    }
    const extractTimelineRows = (structuredSummary)=>{
        const rows = []
        const visit = (node)=>{
            if( node === undefined || node === null ){
                return
            }
            if( Array.isArray(node) ){
                node.forEach(visit)
                return
            }
            if( typeof node !== "object" ){
                return
            }
            const date = typeof node.date === "string" ? node.date : undefined
            const eventText = typeof node.event === "string"
                ? node.event
                : (typeof node.title === "string" ? node.title : undefined)
            if( date || eventText ){
                rows.push({
                    id: node.id ?? `timeline-${rows.length + 1}`,
                    date: normalizeTimelineCell(date),
                    name: normalizeTimelineCell(node.name),
                    event_type: normalizeTimelineCell(node.event_type ?? node.eventType),
                    event: normalizeTimelineCell(eventText),
                    rationale: normalizeTimelineCell(node.rationale),
                    deal: normalizeTimelineCell(node.deal),
                    source: normalizeTimelineCell(node.source),
                    overview: normalizeTimelineCell(node.overview),
                    quote: normalizeTimelineCell(node.quote),
                })
                const target = rows[rows.length - 1]
                Object.entries(node).forEach(([key, value])=>{
                    if( key in target || timelineReservedMetaFields.has(key) || key === "eventType" ){
                        return
                    }
                    if( key === "event_type" || key === "event" || key === "date" ){
                        return
                    }
                    if( value && typeof value === "object" && !Array.isArray(value) ){
                        return
                    }
                    target[key] = normalizeTimelineCell(value)
                })
            }
            Object.values(node).forEach((value)=>{
                if( value && typeof value === "object" ){
                    visit(value)
                }
            })
        }
        visit(structuredSummary)
        rows.sort((a, b)=>{
            const aDate = Date.parse(a.date)
            const bDate = Date.parse(b.date)
            const aValid = Number.isFinite(aDate)
            const bValid = Number.isFinite(bDate)
            if( !aValid && !bValid ){return 0}
            if( !aValid ){return 1}
            if( !bValid ){return -1}
            return aDate - bDate
        })
        return rows.filter((row)=>hasMeaningfulTimelineContent(row))
    }

    const showSummaryContent = props.showSummaryContent !== false

    if(showSummaryContent && primitive.referenceParameters?.summary){
        //summary = <MarkdownEditor ref={summaryRef} initialMarkdown={primitive.referenceParameters?.summary }/>

        const structuredSummaryValue = primitive.referenceParameters?.structured_summary
        const normalizedStructuredSummary = Array.isArray(structuredSummaryValue)
            ? structuredSummaryValue
            : (Array.isArray(structuredSummaryValue?.sections)
                ? structuredSummaryValue.sections.map((section)=>({
                    heading: section?.heading ?? section?.title ?? "Section",
                    content: section?.content ?? "",
                    ids: Array.isArray(section?.ids) ? section.ids : [],
                    quote: Array.isArray(section?.quote) ? section.quote : (section?.quote != null ? [section.quote] : []),
                    id_map: section?.id_map && typeof section.id_map === 'object' && !Array.isArray(section.id_map) ? section.id_map : undefined,
                    anchor_mentions: Array.isArray(section?.anchor_mentions) ? section.anchor_mentions : [],
                }))
                : null)
        const normalizeAnchorId = (value)=>{
            const raw = String(value ?? "").trim().toLowerCase()
            if( !raw ){return ""}
            return raw
                .replace(/^_+|_+$/g, "")
                .replace(/^anchor[:\-_]*/g, "")
                .replace(/^_anchor[:\-_]*/g, "")
                .replace(/[^a-z0-9_.:-]+/g, "_")
                .replace(/^_+|_+$/g, "")
        }
        const classifyAnchorIntent = (anchorId)=>{
            const id = normalizeAnchorId(anchorId)
            if( !id ){return "generic"}
            if( /(timeline|chronology|roadmap|milestone|release)/i.test(id) ){
                return "timeline"
            }
            if( /(financial|finance|revenue|profit|margin|ebit|cash|balance|pnl)/i.test(id) ){
                return "financial_table"
            }
            return "generic"
        }
        const taskFrameworkContext = primitive.referenceParameters?.task_framework_context ?? {}
        const synthesisCatalog = Array.isArray(taskFrameworkContext?.synthesisInputCatalog)
            ? taskFrameworkContext.synthesisInputCatalog
            : []
        const normalizeHints = (value)=>{
            if( !value ){return []}
            const list = Array.isArray(value) ? value : [value]
            return list
                .map((entry)=>(typeof entry === "string" ? entry.trim() : ""))
                .filter(Boolean)
        }
        const excludedTableHints = new Set(["structured_summary", "account_brief", "synthesis"])
        const catalogByInputId = new Map(
            synthesisCatalog
                .filter((entry)=>entry?.inputId)
                .map((entry)=>[entry.inputId, entry])
        )
        const collectHintsFromPrimitiveChain = (sourcePrimitive)=>{
            if( !sourcePrimitive ){return []}
            const queue = [sourcePrimitive]
            const visited = new Set()
            const out = []
            const seen = new Set()
            let depth = 0
            while(queue.length > 0 && depth < 4){
                const levelSize = queue.length
                for(let idx = 0; idx < levelSize; idx++){
                    const current = queue.shift()
                    if( !current ){continue}
                    const currentId = `${current.id ?? current.plainId ?? ""}`
                    if( currentId && visited.has(currentId) ){continue}
                    if( currentId ){visited.add(currentId)}
                    const hints = normalizeHints(current?.referenceParameters?.contentHints)
                    hints.forEach((hint)=>{
                        const key = hint.toLowerCase()
                        if( excludedTableHints.has(key) || seen.has(key) ){
                            return
                        }
                        seen.add(key)
                        out.push(hint)
                    })
                    const parents = Array.isArray(current?.parentPrimitives) ? current.parentPrimitives : []
                    parents.forEach((parent)=>{
                        if( parent ){queue.push(parent)}
                    })
                }
                depth += 1
            }
            return out
        }

        const resolveAnchorPrimitiveIds = (anchorId, section)=>{
            const ids = new Set()
            const normAnchorId = normalizeAnchorId(anchorId)
            if( !normAnchorId ){return []}
            const mentions = Array.isArray(section?.anchor_mentions) ? section.anchor_mentions : []
            const mention = mentions.find((entry)=>normalizeAnchorId(entry?.anchor_id) === normAnchorId)
            if( mention && Array.isArray(mention.input_refs) ){
                mention.input_refs.forEach((inputRef)=>{
                    const mapped = catalogByInputId.get(inputRef)?.primitiveId
                    if( mapped ){
                        ids.add(`${mapped}`)
                    }
                })
            }
            const pathNode = primitive?.primitives?.fromPath?.(`anchor.${normAnchorId}`)
            const relIds = Array.isArray(pathNode?.allIds) ? pathNode.allIds : []
            relIds.forEach((id)=>ids.add(`${id}`))
            return Array.from(ids)
        }

        const buildAnchorTableMarkdown = (anchorId, section)=>{
            const primitiveIds = resolveAnchorPrimitiveIds(anchorId, section)
            if( primitiveIds.length === 0 ){
                return ""
            }
            let items = primitiveIds.map((id)=>MainStore().primitive(id)).filter(Boolean)
            if( items.length === 0 ){
                return ""
            }
            const anchorIntent = classifyAnchorIntent(anchorId)
            const expandViewImports = (sourceItems = [])=>{
                const expanded = []
                const seen = new Set()
                const pushItem = (item)=>{
                    if( !item ){return}
                    const id = `${item.id ?? item.plainId ?? ""}`
                    if( !id || seen.has(id) ){return}
                    seen.add(id)
                    expanded.push(item)
                }
                sourceItems.forEach((item)=>{
                    if( item?.type === "view" ){
                        const processed = item?.itemsForProcessing
                        
                        if( Array.isArray(processed) && processed.length > 0 ){
                            processed.forEach((child)=>pushItem(child))
                        }
                        return
                    }
                    pushItem(item)
                })
                return expanded
            }
            const hints = new Set()
            items.forEach((item)=>{
                const chainHints = collectHintsFromPrimitiveChain(item)
                chainHints.forEach((hint)=>hints.add(hint))
            })
            const mentions = Array.isArray(section?.anchor_mentions) ? section.anchor_mentions : []
            mentions
                .filter((entry)=>normalizeAnchorId(entry?.anchor_id) === normalizeAnchorId(anchorId))
                .forEach((entry)=>{
                    const refs = Array.isArray(entry?.input_refs) ? entry.input_refs : []
                    refs.forEach((inputRef)=>{
                        const catalogEntry = catalogByInputId.get(inputRef)
                        const entryHints = Array.isArray(catalogEntry?.contentHints) ? catalogEntry.contentHints : []
                        entryHints.forEach((hint)=>{
                            if( typeof hint === "string" && hint.trim().length ){
                                const normalized = hint.trim()
                                if( !excludedTableHints.has(normalized.toLowerCase()) ){
                                    hints.add(normalized)
                                }
                            }
                        })
                    })
                })
            if( anchorIntent === "financial_table" ){
                items = expandViewImports(items)
            }
            if( anchorIntent === "financial_table" ){
                const hasFinancialHint = (item)=>{
                    const allHints = [
                        ...collectHintsFromPrimitiveChain(item),
                        ...normalizeHints(item?.referenceParameters?.contentHints)
                    ].map((hint)=>String(hint).toLowerCase())
                    const directRef = item?.referenceParameters ?? {}
                    const hintHasValue = Array.from(hints).some((hint)=>{
                        if( typeof hint !== "string" || !hint.trim().length ){return false}
                        const path = hint.trim()
                        if( directRef[path] !== undefined && directRef[path] !== null ){return true}
                        const key = path.split(".").filter(Boolean).slice(-1)[0]
                        return key ? directRef?.[key] !== undefined && directRef?.[key] !== null : false
                    })
                    return hintHasValue || allHints.some((hint)=>hint.endsWith("_details") || hint.includes("financial") || hint.includes("revenue") || hint.includes("netprofit"))
                }
                items = items.filter((item)=>hasFinancialHint(item))
                if( items.length === 0 ){
                    return ""
                }
            }
            if( anchorIntent === "timeline" ){
                const rows = items
                    .flatMap((item)=>extractTimelineRows(item?.referenceParameters?.structured_summary))
                    .filter((row)=>row && (row.date || row.event))
                if( rows.length === 0 ){
                    return ""
                }
                const columns = ["Date", "Event", "Source"]
                const tableLines = [
                    `|${columns.join("|")}|`,
                    `|${columns.map(()=>"-").join("|")}|`,
                    ...rows.slice(0, 16).map((row)=>`|${(row.date ?? "").replaceAll("|","/")}|${(row.event ?? "").replaceAll("|","/")}|${(row.source ?? "").replaceAll("|","/")}|`)
                ]
                return `\n\n${tableLines.join("\n")}\n\n`
            }
            if( hints.size === 0 ){
                return ""
            }
            const tableMarkdown = buildFieldExtractMarkdownTable({
                items,
                hints: Array.from(hints),
                includeItemLabel: true,
                compact: true
            })
            return typeof tableMarkdown === "string" && tableMarkdown.trim().length
                ? `\n\n${tableMarkdown.trim()}\n\n`
                : ""
        }

        const injectAnchorTablesIntoContent = (content, section)=>{
            if( typeof content !== "string" || !content.trim().length ){
                return content
            }
            const mentions = Array.isArray(section?.anchor_mentions) ? section.anchor_mentions : []
            if( mentions.length === 0 ){
                return content
            }
            let next = content
            let appended = ""
            mentions.forEach((entry)=>{
                const anchorId = normalizeAnchorId(entry?.anchor_id)
                if( !anchorId ){return}
                const table = buildAnchorTableMarkdown(anchorId, section)
                if( !table ){return}
                const escapedAnchorId = anchorId.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
                const patterns = [
                    new RegExp(`\\{\\{\\s*${escapedAnchorId}\\s*\\}\\}`, "gi"),
                    new RegExp(`\\[\\[\\s*anchor\\s*:\\s*${escapedAnchorId}\\s*\\]\\]`, "gi"),
                    new RegExp(`_anchor\\s*:\\s*${escapedAnchorId}\\b`, "gi"),
                    new RegExp(`【\\s*anchor\\s*:\\s*${escapedAnchorId}\\s*】`, "gi"),
                ]
                let replaced = false
                patterns.forEach((pattern)=>{
                    if( pattern.test(next) ){
                        next = next.replace(pattern, table)
                        replaced = true
                    }
                })
                if( !replaced ){
                    appended += table
                }
            })
            return `${next}${appended}`.trim()
        }

        if( normalizedStructuredSummary ){
            if( isTimelineSummary ){
                const timelineRows = extractTimelineRows(normalizedStructuredSummary)
                if( timelineRows.length > 0 ){
                    const hasName = timelineRows.some((row)=>typeof row.name === "string" && row.name.trim().length > 0)
                    const hasRationale = timelineRows.some((row)=>typeof row.rationale === "string" && row.rationale.trim().length > 0)
                    const hasDeal = timelineRows.some((row)=>typeof row.deal === "string" && row.deal.trim().length > 0)
                    const dynamicFieldKeys = Array.from(
                        timelineRows.reduce((acc, row)=>{
                            Object.keys(row).forEach((key)=>{
                                if( !timelineFixedFields.has(key) && !timelineReservedMetaFields.has(key) ){
                                    const value = normalizeTimelineCell(row[key])
                                    if( value.length > 0 && !isPlaceholderTimelineCell(value) ){
                                        acc.add(key)
                                    }
                                }
                            })
                            return acc
                        }, new Set())
                    )
                    const columns = [
                        { title: "Date", field: "date", renderType: "date", width: 150, minWidth: 140 },
                        ...(hasName ? [{ title: "Name", field: "name", width: 180, minWidth: 140 }] : []),
                        { title: "Event", field: "event", minWidth: 260, wrap: true },
                        ...(hasRationale ? [{ title: "Rationale", field: "rationale", minWidth: 260, wrap: true }] : []),
                        ...(hasDeal ? [{ title: "Deal", field: "deal", minWidth: 200, wrap: true }] : []),
                        ...dynamicFieldKeys.map((field)=>({
                            title: titleFromField(field),
                            field,
                            minWidth: 220,
                            wrap: true
                        })),
                        { title: "Source", field: "source", width: 140, minWidth: 120 },
                    ]
                    summary = (
                        <Table
                            compact
                            enableCopy
                            data={timelineRows}
                            columns={columns}
                        />
                    )
                    renderedAsTimelineTable = true
                }
            }
            if( !renderedAsTimelineTable ){
            summary = (
                <StructuredSummaryEditor
                    primitive={primitive}
                    sections={normalizedStructuredSummary}
                    isLocked={isLocked}
                    setIsLocked={setIsLocked}
                    buildCounter={slateBuildCounter}
                    summaryRef={summaryRef}
                    className={summaryMarkdownClassName}
                    buildAnchorTableMarkdown={buildAnchorTableMarkdown}
                />
            )
            }
        }else{

            let plainSummary = primitive.referenceParameters?.summary
            const sources = primitive.getResultSources
            if(sources.length > 0){
                plainSummary += ` [[id:${sources.map(d=>d.id).join(", ")}]]`
            }else{
                const refs = primitive.primitives.ref.allIds
                if(refs.length > 0){
                    plainSummary += ` [[id:${refs.join(", ")}]]`
                }
            }
            summary = <MarkdownEditor ref={summaryRef} initialMarkdown={plainSummary } className={summaryMarkdownClassName}/>
        }
    }

    return  <div className={clsx(["w-full bg-white flex flex-col p-2", props.className])}>
                {props.title !== false && <div className="flex justify-between place-items-center">
                    <PrimitiveCard.EditableHeader primitive={primitive}/>
                </div>}
                {props.showSetting && <Panel collapsable open={false} className="!mt-0 ml-1" title="Settings" titleClassName='flex w-fit text-xs text-gray-500' arrowClass="text-gray-500 ml-0.5 w-4 h-4">
                    <div className="w-full flex-col text-xs my-2 space-y-1 shrink-0">
                        <PrimitiveCard.Parameters
                          primitive={primitive}
                          hidden='summary'
                          editing
                          leftAlign
                          density="sm"
                          className="text-xs text-slate-500"
                          fullList
                        />
                    </div>
                </Panel>}
                    <div className="w-full flex space-x-2 justify-end">
                        {showSummaryContent && results.length > 0 && <SmallButton icon={ClipboardIcon} action={()=>summaryRef.current?.copyToClipboard?.()}/>}
                        <AIProcessButton active='rebuild_summary'  primitive={primitive} />
                    </div>
                    {showSummaryContent ? summary : null}
                    {longSummary && <div className='w-full flex justify-end -mt-1 mb-1'>
                        <div className="bg-gray-50 text-gray-500 hover:text-gray-700 hover:bg-gray-200 p-1 text-xs mr-1" onClick={()=>setPreview(!preview)}>{`Show ${preview ? "more" : "less"}`}</div>
                    </div>}
        </div>
}
